import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { Row } from "@/admin/resources/api";

type Turn = { role?: string; speaker?: string; text?: string; content?: string };
type Trace = {
  turn_index: number;
  user_message: string;
  assistant_response: string;
  pipeline_version: string;
  total_ms: number | null;
  ttfa_ms: number | null;
  endpointing_ms: number | null;
  stt_ms: number | null;
  llm_ttft_ms: number | null;
  tts_ttfb_ms: number | null;
  tool_ms: number | null;
};

const ms = (v: number | null) => (v == null ? "—" : `${v.toLocaleString()} ms`);

/** A call's transcript and per-turn latency, below its details. */
export function CallDetail({ row }: { row: Row }) {
  const turns = (Array.isArray(row.__turns) ? row.__turns : []) as Turn[];
  const plain = (row.__transcript as string) || "";
  const traces = (row.__traces as Trace[]) ?? [];

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Transcript</CardTitle>
        </CardHeader>
        <CardContent>
          {turns.length > 0 ? (
            <div className="space-y-2">
              {turns.map((t, i) => {
                const agent = t.role === "assistant" || t.speaker === "agent";
                return (
                  <div key={i} className={cn("rounded-md p-3", agent && "bg-muted/50")}>
                    <p
                      className={cn(
                        "mb-1 text-[10px] font-semibold uppercase tracking-wide",
                        agent ? "text-primary" : "text-muted-foreground",
                      )}
                    >
                      {agent ? "Agent" : "Caller"}
                    </p>
                    <p className="text-sm">{t.text || t.content || ""}</p>
                  </div>
                );
              })}
            </div>
          ) : plain ? (
            <p className="whitespace-pre-wrap text-sm">{plain}</p>
          ) : (
            <p className="text-sm text-muted-foreground">No transcript.</p>
          )}
        </CardContent>
      </Card>

      {traces.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Latency per turn</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Turn</th>
                  <th className="px-4 py-2 font-medium">Caller said</th>
                  <th className="px-4 py-2 font-medium text-right">Total</th>
                  <th className="px-4 py-2 font-medium text-right">First audio</th>
                  <th className="px-4 py-2 font-medium text-right">STT</th>
                  <th className="px-4 py-2 font-medium text-right">LLM</th>
                  <th className="px-4 py-2 font-medium text-right">TTS</th>
                  <th className="px-4 py-2 font-medium text-right">Tools</th>
                </tr>
              </thead>
              <tbody>
                {traces.map((t) => (
                  <tr key={t.turn_index} className="border-b last:border-0">
                    <td className="px-4 py-2 tabular-nums">{t.turn_index}</td>
                    <td className="max-w-xs px-4 py-2">
                      <span className="line-clamp-2">{t.user_message || "—"}</span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{ms(t.total_ms)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{ms(t.ttfa_ms)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{ms(t.stt_ms)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{ms(t.llm_ttft_ms)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{ms(t.tts_ttfb_ms)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{ms(t.tool_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </>
  );
}
